import * as styles from "./AmendmentEmployeesTab.css";

import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { JSX } from "react";

import { Badge } from "@frontend/components/atoms/Badge";
import { Button } from "@frontend/components/atoms/Button";
import { Checkbox } from "@frontend/components/atoms/Checkbox";
import { Heading } from "@frontend/components/atoms/Heading";
import { Icon } from "@frontend/components/atoms/Icon";
import type { IconName } from "@frontend/components/atoms/Icon";
import { Input } from "@frontend/components/atoms/Input";
import { Panel } from "@frontend/components/atoms/Panel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@frontend/components/atoms/Select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import { Pagination } from "@frontend/components/molecules/Pagination";
import {
  Toolbar,
  ToolbarSection,
} from "@frontend/components/molecules/Toolbar";
import {
  DataTable,
  type DataTableRowMenuItem,
  createDataTableColumnHelper,
} from "@frontend/components/organisms/DataTable";
import { PdfViewerDialog } from "@frontend/components/organisms/PdfViewer";
import { useDebouncedCallback } from "@frontend/hooks/useDebouncedCallback";
import { url } from "@frontend/lib/http";
import { Trans, useLingui } from "@lingui/react/macro";

import { amendmentApi } from "../services";
import {
  amendmentEmployeesAtom,
  amendmentEmployeesPageCountAtom,
  changeAmendmentStatusAtom,
  clearAmendmentBatchAtom,
  generateAmendmentAtom,
  generateAmendmentBatchAtom,
  loadAmendmentEmployeesAtom,
  previewAmendmentBundleAtom,
  setAmendmentEmployeesDirectionAtom,
  setAmendmentEmployeesPageAtom,
  setAmendmentEmployeesPerPageAtom,
  setAmendmentEmployeesResponsableAtom,
  setAmendmentEmployeesSearchAtom,
  setAmendmentEmployeesStatusAtom,
  toggleAmendmentEmployeeSelectionAtom,
  toggleAmendmentPageSelectionAtom,
} from "../stores/employees";
import type { AmendmentEmployeeRow, AmendmentPerPage } from "../types";
import { AMENDMENT_PER_PAGE_CHOICES } from "../types";
import { AmendmentBatchReport } from "./AmendmentBatchReport";
import { AmendmentGenerateDialog } from "./AmendmentGenerateDialog";
import { AmendmentHistoryDialog } from "./AmendmentHistoryDialog";
import { AmendmentWeeksCell } from "./AmendmentWeeksCell";

const helper = createDataTableColumnHelper<AmendmentEmployeeRow>();

const SEARCH_DEBOUNCE = 300;

const EVERY = "all";

const STATUS_ICONS: Record<string, IconName> = {
  sent: "Send",
  signed: "Signature",
  archived: "Archive",
};

type EmployeeAction = DataTableRowMenuItem & {
  inRow: boolean;
  run: () => void;
};

function day(isoDate: string | null): string {
  if (null === isoDate) {
    return "-";
  }

  const [year, month, dayOfMonth] = isoDate.split("-");

  return `${dayOfMonth}.${month}.${year}`;
}

export function AmendmentEmployeesTab(): JSX.Element {
  const { t } = useLingui();

  const {
    rows,
    total,
    page,
    perPage,
    search,
    responsable,
    status,
    direction,
    responsables,
    statuses,
    selection,
    loading,
    working,
    error,
    batch,
  } = useAtomValue(amendmentEmployeesAtom);
  const pageCount = useAtomValue(amendmentEmployeesPageCountAtom);

  const load = useSetAtom(loadAmendmentEmployeesAtom);
  const setSearch = useSetAtom(setAmendmentEmployeesSearchAtom);
  const setResponsable = useSetAtom(setAmendmentEmployeesResponsableAtom);
  const setStatus = useSetAtom(setAmendmentEmployeesStatusAtom);
  const setDirection = useSetAtom(setAmendmentEmployeesDirectionAtom);
  const setPerPage = useSetAtom(setAmendmentEmployeesPerPageAtom);
  const setPage = useSetAtom(setAmendmentEmployeesPageAtom);
  const toggleRow = useSetAtom(toggleAmendmentEmployeeSelectionAtom);
  const togglePage = useSetAtom(toggleAmendmentPageSelectionAtom);
  const generate = useSetAtom(generateAmendmentAtom);
  const changeStatus = useSetAtom(changeAmendmentStatusAtom);
  const generateBatch = useSetAtom(generateAmendmentBatchAtom);
  const previewBundle = useSetAtom(previewAmendmentBundleAtom);
  const clearBatch = useSetAtom(clearAmendmentBatchAtom);

  const [confirmingBundle, setConfirmingBundle] = useState(false);
  const [historyOf, setHistoryOf] = useState<number | null>(null);
  const [viewing, setViewing] = useState<{
    amendmentId: number;
    name: string;
  } | null>(null);
  const [generating, setGenerating] = useState<number | "selection" | null>(
    null,
  );

  const [typed, setTyped] = useState(search);

  const searchLater = useDebouncedCallback(
    (next: string) => setSearch(next),
    SEARCH_DEBOUNCE,
  );

  useEffect(() => {
    void load();
  }, [load]);

  const statusLabels = useMemo(
    () =>
      new Map(
        statuses.map((choice) => [
          choice.status,
          choice.label ?? choice.status,
        ]),
      ),
    [statuses],
  );

  const pageSelected =
    rows.length > 0 && rows.every((row) => selection.includes(row.employeeId));

  const { year } = useAtomValue(amendmentEmployeesAtom);

  const noWeekOwedHint = t`The subscription contract leaves no closure week in ${year}: no amendment can be generated.`;

  const actionsOf = useCallback(
    (row: AmendmentEmployeeRow): EmployeeAction[] => {
      const { amendment, contractActive, employeeId, subscriptions } = row;
      const generable =
        contractActive &&
        !subscriptions.some((subscription) => subscription.owesNoWeek);

      const actions: EmployeeAction[] = [];

      if (null === amendment) {
        if (generable) {
          actions.push({
            id: "generate",
            label: t`Generate`,
            icon: "Wand2",
            inRow: true,
            disabled: working,
            run: () => setGenerating(employeeId),
          });
        }
      } else {
        if (amendment.needsReview && generable) {
          actions.push({
            id: "regenerate",
            label: t`Regenerate`,
            icon: "RefreshCw",
            inRow: true,
            disabled: working,
            run: () => setGenerating(employeeId),
          });
        }

        actions.push({
          id: "download",
          label: t`Download`,
          icon: "Download",
          inRow: true,
          run: () => {
            window.location.href = amendmentApi.documentUrl(amendment.id);
          },
        });

        actions.push({
          id: "view",
          label: t`View the PDF`,
          icon: "Eye",
          inRow: false,
          run: () =>
            setViewing({
              amendmentId: amendment.id,
              name: `avenant_${employeeId}_${year}.pdf`,
            }),
        });

        const archiving =
          "archived" === amendment.status ||
          amendment.nextStatuses.includes("archived");

        for (const next of amendment.nextStatuses) {
          const label = statusLabels.get(next) ?? next;

          actions.push({
            id: `status:${next}`,
            label: t`Mark as ${label}`,
            icon:
              "archived" === amendment.status
                ? "ArchiveRestore"
                : (STATUS_ICONS[next] ?? "ArrowRight"),
            inRow: !archiving,
            disabled: working,
            run: () => void changeStatus(amendment.id, next),
          });
        }

        actions.push({
          id: "history",
          label: t`Amendment history`,
          icon: "History",
          inRow: false,
          separatorBefore: true,
          run: () => setHistoryOf(amendment.id),
        });
      }

      actions.push({
        id: "employee",
        label: t`Open the collaborateur file`,
        icon: "UserRound",
        inRow: false,
        separatorBefore: actions.length > 0,
        run: () => {
          window.location.href = url("azuris_employee_edit", {
            id: employeeId,
          });
        },
      });

      return actions;
    },
    [t, year, statusLabels, working, changeStatus],
  );

  const columns = useMemo(
    () =>
      helper.columns([
        helper.display({
          id: "select",
          enableSorting: false,
          meta: { width: "52px" },
          header: () => (
            <span className={styles.selectCell}>
              <Checkbox
                checked={pageSelected}
                aria-label={t`Select the rows of this page`}
                onCheckedChange={(checked) => togglePage(Boolean(checked))}
              />
            </span>
          ),
          cell: ({ row }) => (
            <span className={styles.selectCell}>
              <Checkbox
                checked={selection.includes(row.original.employeeId)}
                aria-label={t`Select this collaborateur`}
                onCheckedChange={() => toggleRow(row.original.employeeId)}
              />
            </span>
          ),
        }),
        helper.accessor("employeeId", {
          header: t`Id`,
          enableSorting: false,
          meta: { minWidth: "70px", maxWidth: "90px" },
        }),
        helper.accessor("employeeName", {
          header: t`Collaborateur`,
          enableSorting: false,
          meta: { minWidth: "160px", truncate: true },
          cell: ({ row, getValue }) => (
            <Heading
              name={getValue() ?? "-"}
              href={url("azuris_employee_edit", {
                id: row.original.employeeId,
              })}
            />
          ),
        }),
        helper.accessor("responsableName", {
          header: t`Responsable`,
          enableSorting: false,
          meta: { minWidth: "120px", maxWidth: "200px", truncate: true },
          cell: ({ getValue }) => getValue() ?? "-",
        }),
        helper.display({
          id: "subscriptions",
          header: t`Subscriptions`,
          enableSorting: false,
          meta: { minWidth: "160px", maxWidth: "280px" },
          cell: ({ row }) => (
            <div className={styles.subscriptions}>
              {row.original.subscriptions.map((subscription) => (
                <Badge
                  key={subscription.id}
                  size="sm"
                  variant={subscription.owesNoWeek ? "warning" : "neutral"}
                  icon={subscription.owesNoWeek ? "TriangleAlert" : undefined}
                  className={styles.subscription}
                  title={[
                    subscription.name
                      ? `${subscription.name} (${subscription.id})`
                      : String(subscription.id),
                    subscription.owesNoWeek ? noWeekOwedHint : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                >
                  {subscription.name ? (
                    <>
                      <span className={styles.subscriptionName}>
                        {subscription.name}
                      </span>
                      <span className={styles.subscriptionNumber}>
                        ({subscription.id})
                      </span>
                    </>
                  ) : (
                    subscription.id
                  )}
                </Badge>
              ))}
            </div>
          ),
        }),
        helper.accessor("status", {
          header: t`Status`,
          enableSorting: false,
          meta: { minWidth: "130px", maxWidth: "190px" },
          cell: ({ row }) => {
            const { amendment, weekDiff, status } = row.original;

            return (
              <span className={styles.status}>
                <Badge
                  size="sm"
                  variant={
                    "signed" === status
                      ? "success"
                      : "to_validate" === status
                        ? "neutral"
                        : "info"
                  }
                >
                  {amendment?.statusLabel ?? statusLabels.get(status) ?? status}
                </Badge>

                {amendment?.warning ? (
                  <Badge
                    size="sm"
                    variant="warning"
                    title={t`Generated while a subscription disagreed with its agreed count`}
                  >
                    <Icon name="TriangleAlert" size="sm" />
                  </Badge>
                ) : null}

                {amendment?.needsReview ? (
                  <Badge
                    size="sm"
                    variant="error"
                    title={
                      null === weekDiff
                        ? t`The closure weeks changed since it was generated`
                        : t`Weeks added: ${weekDiff.addedWeeks.join(", ") || "-"} · removed: ${weekDiff.removedWeeks.join(", ") || "-"}`
                    }
                  >
                    <Icon name="RefreshCw" size="sm" />
                  </Badge>
                ) : null}
              </span>
            );
          },
        }),
        helper.display({
          id: "weekCount",
          header: t`Weeks`,
          enableSorting: false,
          meta: { minWidth: "80px", maxWidth: "110px" },
          cell: ({ row }) => (
            <AmendmentWeeksCell
              label={String(row.original.amendment?.weekCount ?? "-")}
              periods={row.original.periods}
            />
          ),
        }),
        helper.display({
          id: "generatedAt",
          header: t`Generated on`,
          enableSorting: false,
          meta: { minWidth: "95px", maxWidth: "110px" },
          cell: ({ row }) => day(row.original.amendment?.generatedAt ?? null),
        }),
        helper.display({
          id: "sentAt",
          header: t`Sent on`,
          enableSorting: false,
          meta: { minWidth: "95px", maxWidth: "110px" },
          cell: ({ row }) => day(row.original.amendment?.sentAt ?? null),
        }),
        helper.display({
          id: "signedAt",
          header: t`Signed on`,
          enableSorting: false,
          meta: { minWidth: "95px", maxWidth: "110px" },
          cell: ({ row }) => day(row.original.amendment?.signedAt ?? null),
        }),
        helper.display({
          id: "actions",
          header: t`Action`,
          enableSorting: false,
          meta: { minWidth: "110px", maxWidth: "120px" },
          cell: ({ row }) => {
            const { amendment, contractActive, subscriptions } = row.original;
            const inRow = actionsOf(row.original).filter(
              (action) => action.inRow,
            );

            if (null === amendment && 0 === inRow.length) {
              return contractActive &&
                subscriptions.some(
                  (subscription) => subscription.owesNoWeek,
                ) ? (
                <span title={noWeekOwedHint}>
                  <Icon name="TriangleAlert" size="sm" />
                </span>
              ) : (
                <span title={t`No contract in force to amend`}>
                  <Icon name="Ban" size="sm" />
                </span>
              );
            }

            return (
              <span className={styles.rowActions}>
                {inRow.map((action) => (
                  <Tooltip key={action.id}>
                    <TooltipTrigger
                      render={
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={action.label}
                          disabled={action.disabled}
                          onClick={action.run}
                        >
                          <Icon name={action.icon ?? "ArrowRight"} size="sm" />
                        </Button>
                      }
                    />

                    <TooltipContent side="top">{action.label}</TooltipContent>
                  </Tooltip>
                ))}
              </span>
            );
          },
        }),
      ]),
    [
      t,
      noWeekOwedHint,
      selection,
      pageSelected,
      statusLabels,
      toggleRow,
      togglePage,
      actionsOf,
    ],
  );

  return (
    <>
      {error ? (
        <p className={styles.alert} role="alert">
          {error}
        </p>
      ) : null}

      <Panel overflow="visible">
        <Toolbar wrap={false}>
          <ToolbarSection className={styles.filters}>
            <Input
              className={styles.search}
              value={typed}
              placeholder={t`Search a collaborateur or an identifier`}
              leftSection={<Icon name="Search" size="sm" />}
              onValueChange={(next) => {
                setTyped(next);
                searchLater(next);
              }}
            />

            <Select
              items={[
                { label: t`Every responsable`, value: EVERY },
                ...responsables.map((choice) => ({
                  label: choice.label,
                  value: String(choice.id),
                })),
              ]}
              value={null === responsable ? EVERY : String(responsable)}
              onValueChange={(next) =>
                setResponsable(EVERY === next ? null : Number(next))
              }
            >
              <SelectTrigger className={styles.filter}>
                <SelectValue />
              </SelectTrigger>

              <SelectContent alignItemWithTrigger={false} popupWidth="content">
                <SelectItem value={EVERY}>{t`Every responsable`}</SelectItem>
                {responsables.map((choice) => (
                  <SelectItem key={choice.id} value={String(choice.id)}>
                    {choice.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              items={[
                { label: t`Every status`, value: EVERY },
                ...statuses.map((choice) => ({
                  label: choice.label ?? choice.status,
                  value: choice.status,
                })),
              ]}
              value={null === status ? EVERY : status}
              onValueChange={(next) => setStatus(EVERY === next ? null : next)}
            >
              <SelectTrigger className={styles.filter}>
                <SelectValue />
              </SelectTrigger>

              <SelectContent alignItemWithTrigger={false} popupWidth="content">
                <SelectItem value={EVERY}>{t`Every status`}</SelectItem>
                {statuses.map((choice) => (
                  <SelectItem key={choice.status} value={choice.status}>
                    {choice.label ?? choice.status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </ToolbarSection>

          <ToolbarSection>
            <Button
              size="sm"
              variant="neutral"
              title={t`Sort on the identifier`}
              onClick={() => setDirection("asc" === direction ? "desc" : "asc")}
            >
              <Icon
                name={"asc" === direction ? "ArrowDownAZ" : "ArrowUpAZ"}
                size="sm"
              />
            </Button>

            <Button
              size="sm"
              variant="neutral"
              onClick={() => {
                window.location.href = amendmentApi.exportUrl(year);
              }}
            >
              <Icon name="FileSpreadsheet" size="sm" />
              <Trans>Export</Trans>
            </Button>

            <Select
              items={AMENDMENT_PER_PAGE_CHOICES.map((size) => ({
                label: String(size),
                value: String(size),
              }))}
              value={String(perPage)}
              onValueChange={(next) =>
                setPerPage(Number(next) as AmendmentPerPage)
              }
            >
              <SelectTrigger className={styles.pageSize}>
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                {AMENDMENT_PER_PAGE_CHOICES.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </ToolbarSection>
        </Toolbar>

        <div className={styles.batchBar}>
          <Button
            size="sm"
            variant="primary"
            disabled={working || 0 === selection.length}
            onClick={() => {
              setConfirmingBundle(false);
              setGenerating("selection");
            }}
          >
            <Trans>Generate the selection</Trans>
          </Button>

          <Button
            size="sm"
            variant="neutral"
            disabled={working || 0 === selection.length}
            onClick={() => {
              setConfirmingBundle(true);
              void previewBundle();
            }}
          >
            <Icon name="FileArchive" size="sm" />
            <Trans>Download the selection</Trans>
          </Button>

          <span className={styles.batchCount}>
            <Trans>{selection.length} row(s) selected</Trans>
          </span>
        </div>

        <DataTable
          columns={columns}
          data={rows}
          getRowId={(row) => String(row.employeeId)}
          rowMenu={actionsOf}
          onRowMenuSelect={(id, row) =>
            actionsOf(row)
              .find((action) => action.id === id)
              ?.run()
          }
          loading={loading}
          loadingRowCount={perPage}
          emptyMessage={
            <Trans>No collaborateur is concerned by a closure this year.</Trans>
          }
          columnBorders
          bordered={false}
          scroll="page"
          stickyHeader
          stickyOffset="var(--layout-header-height, 0px)"
        />
      </Panel>

      <Pagination
        page={page}
        pageCount={pageCount}
        totalItems={total}
        pageSize={perPage}
        align="end"
        onPageChange={(next) => setPage(next)}
      />

      <AmendmentBatchReport
        result={batch}
        confirmLabel={confirmingBundle ? t`Download` : undefined}
        onConfirm={
          confirmingBundle
            ? () => {
                clearBatch();
                void amendmentApi.downloadBundle(year, selection);
              }
            : undefined
        }
        onClose={() => clearBatch()}
      />

      <AmendmentGenerateDialog
        count={
          null === generating
            ? 0
            : "selection" === generating
              ? selection.length
              : 1
        }
        onCancel={() => setGenerating(null)}
        onConfirm={(generatedOn) => {
          if ("selection" === generating) {
            void generateBatch(generatedOn);
          } else if (null !== generating) {
            void generate(generating, generatedOn);
          }

          setGenerating(null);
        }}
      />

      <AmendmentHistoryDialog
        key={historyOf}
        amendmentId={historyOf}
        onClose={() => setHistoryOf(null)}
      />

      {null === viewing ? null : (
        <PdfViewerDialog
          open
          src={amendmentApi.documentUrl(viewing.amendmentId)}
          name={viewing.name}
          defaultZoom="fit-page"
          onOpenChange={(next) => {
            if (!next) {
              setViewing(null);
            }
          }}
        />
      )}
    </>
  );
}
